'use client'

import { useState, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { Users, Loader2, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { createBookingSessionAction, selectPropertyAndRoom } from '@/services/booking-actions'
import type { PropertyDetailData } from '@/services/property-detail'
import type { AvailabilityMap } from './PropertyDetailClient'
import { RoomImageCarousel } from './RoomImageCarousel'

interface PropertyRoomTypesProps {
  property: PropertyDetailData
  tenantId: string
  locale: string
  checkIn?: string
  checkOut?: string
  adults?: number
  children?: number
  nights?: number
  couponCode?: string
  discount?: { discountType: string; discountValue: number }
  /**
   * When set, the coupon discount only applies to these room type IDs.
   * Other rooms render at full price even when `discount` is provided.
   * Undefined = no room-type restriction (apply to all).
   */
  allowedRoomTypeIds?: string[]
  availability?: AvailabilityMap
  sessionCode?: string
}

export function PropertyRoomTypes({
  property,
  tenantId,
  locale: _locale,
  checkIn,
  checkOut,
  adults = 2,
  children = 0,
  nights,
  couponCode,
  discount,
  allowedRoomTypeIds,
  availability,
  sessionCode: existingSessionCode,
}: PropertyRoomTypesProps) {
  const t = useTranslations('booking.propertyDetail')
  const tRoom = useTranslations('booking.room')
  const router = useRouter()
  const [loadingRoomTypeId, setLoadingRoomTypeId] = useState<string | null>(null)

  const handleSelectRoom = useCallback(async (roomType: PropertyDetailData['roomTypes'][0]) => {
    if (!checkIn || !checkOut || !availability) return

    const roomAvail = availability[roomType.id]
    if (!roomAvail || roomAvail.available <= 0) return

    setLoadingRoomTypeId(roomType.id)
    try {
      // Use existing session code from search flow, or create a new one
      let finalSessionCode = existingSessionCode

      if (!finalSessionCode) {
        const sessionResult = await createBookingSessionAction({
          tenantId,
          checkIn,
          checkOut,
          adults,
          children,
          couponCode,
        })

        if (!sessionResult.success || !sessionResult.sessionCode) {
          console.error('Failed to create session:', sessionResult.error)
          setLoadingRoomTypeId(null)
          return
        }
        finalSessionCode = sessionResult.sessionCode
      }

      const selectResult = await selectPropertyAndRoom(
        finalSessionCode,
        property.id,
        {
          roomTypeId: roomType.id,
          totalAmount: roomAvail.totalRate,
          averageRate: roomAvail.averageRate,
          currency: roomAvail.currency,
          roomTypeName: roomType.name_en,
        }
      )

      if (selectResult.success) {
        router.push(`/booking/checkout/${finalSessionCode}`)
      } else {
        console.error('Failed to select room:', selectResult.error)
        setLoadingRoomTypeId(null)
      }
    } catch (error) {
      console.error('Error selecting room:', error)
      setLoadingRoomTypeId(null)
    }
  }, [checkIn, checkOut, adults, children, tenantId, property.id, availability, couponCode, existingSessionCode, router])

  const hasAvailability = !!availability && !!checkIn && !!checkOut
  const totalGuests = adults + children

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {t('roomTypes')}
      </h2>

      <div className="space-y-4">
        {property.roomTypes.map((roomType) => {
          const roomAvail = availability?.[roomType.id]
          const isAvailable = roomAvail && roomAvail.available > 0
          const meetsOccupancy = roomType.max_occupancy >= totalGuests
          const isLoading = loadingRoomTypeId === roomType.id
          // Per-room discount: only render the strikethrough/discount price
          // when the coupon's room-type filter (if any) includes this room.
          const roomDiscount = (
            !discount
              ? undefined
              : (allowedRoomTypeIds && !allowedRoomTypeIds.includes(roomType.id))
                ? undefined
                : discount
          )

          return (
            <div
              key={roomType.id}
              className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 overflow-hidden"
            >
              <div className="flex flex-col sm:flex-row">
                {/* Room Image Carousel */}
                <div className="sm:w-56 h-44 sm:h-auto bg-gray-100 dark:bg-gray-700 relative shrink-0">
                  <RoomImageCarousel
                    images={roomType.images}
                    roomName={roomType.name_en}
                  />
                </div>

                {/* Room Info */}
                <div className="flex-1 p-4 flex flex-col">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {roomType.name_en}
                    </h3>
                    {roomType.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                        {roomType.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span>{tRoom('upToGuests', { count: roomType.max_occupancy })}</span>
                      </div>
                      {hasAvailability && isAvailable && (
                        <span className={`font-medium ${
                          roomAvail.available <= 5
                            ? 'text-orange-600 dark:text-orange-400'
                            : 'text-green-600 dark:text-green-500'
                        }`}>
                          {roomAvail.available <= 5 ? (
                            <>
                              <AlertTriangle className="h-3.5 w-3.5 inline mr-0.5" />
                              {tRoom('roomsLeft', { count: roomAvail.available })}
                            </>
                          ) : (
                            tRoom('available', { count: roomAvail.available })
                          )}
                        </span>
                      )}
                    </div>

                    {/* Amenities tags */}
                    {roomType.amenities.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {roomType.amenities.slice(0, 5).map((amenity, i) => (
                          <span
                            key={i}
                            className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded"
                          >
                            {amenity}
                          </span>
                        ))}
                        {roomType.amenities.length > 5 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {tRoom('moreAmenities', { count: roomType.amenities.length - 5 })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Price & Select */}
                  <div className="flex items-end justify-between mt-4 pt-3 border-t dark:border-gray-700">
                    <div>
                      {hasAvailability && isAvailable ? (
                        roomDiscount?.discountType === 'percentage' ? (
                          <>
                            <p className="text-sm text-gray-500 dark:text-gray-400 line-through">
                              {roomAvail.currency} {roomAvail.totalRate.toFixed(2)}
                            </p>
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                              {roomAvail.currency} {(roomAvail.totalRate * (1 - roomDiscount.discountValue / 100)).toFixed(2)}
                            </p>
                            <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                              {tRoom('percentOff', { percent: roomDiscount.discountValue })}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {nights} {nights === 1 ? 'night' : 'nights'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {roomAvail.currency} {(roomAvail.averageRate * (1 - roomDiscount.discountValue / 100)).toFixed(2)} {tRoom('perNightAvg')}
                            </p>
                            {roomAvail.taxTotal > 0 && (
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                {tRoom('taxesIncluded')} &middot; {roomAvail.currency} {roomAvail.taxTotal.toFixed(2)}
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                              {roomAvail.currency} {roomAvail.totalRate.toFixed(2)}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {nights} {nights === 1 ? 'night' : 'nights'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {roomAvail.currency} {roomAvail.averageRate.toFixed(2)} {tRoom('perNightAvg')}
                            </p>
                            {roomAvail.taxTotal > 0 && (
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                {tRoom('taxesIncluded')} &middot; {roomAvail.currency} {roomAvail.taxTotal.toFixed(2)}
                              </p>
                            )}
                          </>
                        )
                      ) : (
                        <>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('from')}</p>
                          <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                            {roomType.currency} {roomType.minRate.toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('perNight')}</p>
                        </>
                      )}
                    </div>

                    {hasAvailability ? (
                      isAvailable && meetsOccupancy ? (
                        <Button
                          onClick={() => handleSelectRoom(roomType)}
                          disabled={isLoading || !!loadingRoomTypeId}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              {tRoom('selecting')}
                            </>
                          ) : (
                            tRoom('selectRoom')
                          )}
                        </Button>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {!meetsOccupancy ? t('notEnoughCapacity') : t('soldOut')}
                        </span>
                      )
                    ) : (
                      <a href="#booking-sidebar" className="hidden sm:block">
                        <Button variant="outline" size="sm">
                          {t('checkAvailability')}
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
